import { CartLinesUpdateEvent } from '@shopify/events';

class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.select = this.querySelector('select[name="id"]');
    this.button = this.querySelector('button[type="submit"]');
    this.price = this.querySelector('[data-upsell-price]');
    this.comparePrice = this.querySelector('[data-upsell-compare-price]');
    this.status = this.querySelector('[role="status"]');
    this.image = this.querySelector('.cart-upsell__image');
    this.bundleSelect = this.querySelector('[data-upsell-bundle]');
    this.pairsContainer = this.querySelector('[data-upsell-pairs]');
    this.pairTemplate = this.querySelector('[data-upsell-pair-template]');
    this.bundleNote = this.querySelector('[data-upsell-bundle-note]');
    this.pairChoices ??= [];

    this.form?.addEventListener('submit', this.handleSubmit);
    this.select?.addEventListener('change', this.handleVariantChange);
    this.bundleSelect?.addEventListener('change', this.handleBundleChange);
    this.pairsContainer?.addEventListener('change', this.handlePairVariantChange);
    if (this.bundleSelect) this.handleBundleChange();
    else this.handleVariantChange();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.select?.removeEventListener('change', this.handleVariantChange);
    this.bundleSelect?.removeEventListener('change', this.handleBundleChange);
    this.pairsContainer?.removeEventListener('change', this.handlePairVariantChange);
  }

  getPairSelects = () => Array.from(this.pairsContainer?.querySelectorAll('[data-upsell-pair-variant]') || []);

  getBundleQuantity = () => {
    const quantity = Number(this.bundleSelect?.value);
    return [1, 4, 8].includes(quantity) ? quantity : 0;
  };

  getSelectedVariantId = (select) => {
    const option = select?.selectedOptions?.[0];
    const variantId = Number(select?.value);
    if (!option || option.disabled || option.value !== select.value
      || !/^\d+$/.test(select.value) || !Number.isSafeInteger(variantId) || variantId <= 0) return null;
    return variantId;
  };

  getCartItems = () => {
    if (!this.bundleSelect) {
      const variantId = this.getSelectedVariantId(this.select);
      return variantId ? [{ id: variantId, quantity: 1 }] : null;
    }

    const quantity = this.getBundleQuantity();
    const selects = this.getPairSelects();
    if (!quantity || selects.length !== quantity) return null;

    const quantities = new Map();
    for (const select of selects) {
      const variantId = this.getSelectedVariantId(select);
      if (!variantId) return null;
      quantities.set(variantId, (quantities.get(variantId) || 0) + 1);
    }
    return Array.from(quantities, ([id, pairQuantity]) => ({ id, quantity: pairQuantity }));
  };

  rememberPairChoices = () => {
    this.getPairSelects().forEach((select, index) => { this.pairChoices[index] = select.value; });
  };

  handleBundleChange = () => {
    if (this.isSubmitting || !this.pairsContainer) return;
    this.rememberPairChoices();
    const quantity = this.getBundleQuantity();
    const rows = Array.from(this.pairsContainer.querySelectorAll('[data-upsell-pair]'));
    rows.slice(quantity).forEach((row) => row.remove());

    for (let index = 0; index < quantity; index += 1) {
      const row = rows[index] || this.pairTemplate?.content.firstElementChild?.cloneNode(true);
      if (!row) break;
      if (!rows[index]) this.pairsContainer.appendChild(row);
      const label = row.querySelector('[data-upsell-pair-label]');
      if (label) label.textContent = `Pair ${index + 1}`;
      const select = row.querySelector('[data-upsell-pair-variant]');
      if (select && Object.hasOwn(this.pairChoices, index)) select.value = this.pairChoices[index];
    }

    this.updateBundleOffer();
    const firstSelected = this.getPairSelects().find((select) => this.getSelectedVariantId(select));
    if (firstSelected) this.updateImage(firstSelected);
  };

  handlePairVariantChange = (event) => {
    if (this.isSubmitting || !event.target?.matches('[data-upsell-pair-variant]')) return;
    this.rememberPairChoices();
    this.updateBundleOffer();
    this.updateImage(event.target);
  };

  updateBundleOffer = () => {
    const option = this.bundleSelect?.selectedOptions?.[0];
    if (option?.dataset.price && this.price) this.price.textContent = option.dataset.price;
    if (option?.dataset.comparePrice && this.comparePrice) this.comparePrice.textContent = option.dataset.comparePrice;
    if (this.bundleNote) this.bundleNote.textContent = option?.dataset.note || '';
    if (this.button) this.button.disabled = Boolean(this.isSubmitting) || !this.getCartItems();
  };

  updateImage = (select) => {
    const option = select?.selectedOptions?.[0];
    if (option?.dataset.image && this.image) {
      this.image.src = option.dataset.image;
      this.image.removeAttribute('srcset');
      this.image.alt = option.dataset.imageAlt || '';
    }
  };

  lockSelectionControls = () => {
    this.pendingControls = [this.select, this.bundleSelect, ...this.getPairSelects()]
      .filter(Boolean).map((control) => ({ control, wasDisabled: Boolean(control.disabled) }));
    this.pendingControls.forEach(({ control }) => { control.disabled = true; });
  };

  restoreSelectionControls = () => {
    this.pendingControls?.forEach(({ control, wasDisabled }) => { control.disabled = wasDisabled; });
    this.pendingControls = null;
  };

  handleVariantChange = () => {
    const option = this.select?.selectedOptions?.[0];
    if (option?.dataset.price && this.price) this.price.textContent = option.dataset.price;
    if (this.dataset.requiresVariantSelection === 'true' && this.button && !this.isSubmitting) {
      this.button.disabled = !this.getSelectedVariantId(this.select);
    }
    this.updateImage(this.select);
  };

  handleSubmit = async (event) => {
    event.preventDefault();

    if (this.isSubmitting || !this.button) return;
    const cartItems = this.getCartItems();
    if (!cartItems) {
      if (this.bundleSelect) this.updateBundleOffer();
      return;
    }

    const originalLabel = this.button.innerHTML;
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => item instanceof HTMLElement ? item.dataset.sectionId : '')
      .filter(Boolean);

    this.button.disabled = true;
    this.isSubmitting = true;
    this.lockSelectionControls();
    this.button.textContent = 'Adding…';
    if (this.status) this.status.textContent = '';

    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: cartItems.map((item) => ({ merchandiseId: item.id.toString(), quantity: item.quantity })),
      promise: deferredEventPromise.promise,
    }));

    let addSucceeded = false;
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: cartItems,
          sections: [...new Set(sectionIds)].join(','),
          sections_url: window.location.pathname,
        }),
      });
      const addedItem = await response.json();

      if (!response.ok || addedItem.status) {
        throw new Error(addedItem.description || addedItem.message || 'Could not add this item.');
      }
      addSucceeded = true;

      const cartResponse = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('Could not refresh your cart.');

      const cart = await cartResponse.json();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: addedItem.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'cart-upsell',
          sourceId: this.dataset.productId,
          didError: false,
        },
      });
    } catch (error) {
      deferredEventPromise.reject(error);
      if (addSucceeded) {
        this.button.textContent = 'Added — refresh cart';
        if (this.status) this.status.textContent = 'Your items were added. Refresh the cart to view them.';
        return;
      }
      this.isSubmitting = false;
      this.restoreSelectionControls();
      this.button.disabled = false;
      this.button.innerHTML = originalLabel;
      this.price = this.querySelector('[data-upsell-price]');
      this.comparePrice = this.querySelector('[data-upsell-compare-price]');
      if (this.bundleSelect) this.handleBundleChange();
      else this.handleVariantChange();
      if (this.status) this.status.textContent = error instanceof Error ? error.message : 'Could not add this item.';
    }
  };
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
